import { defineStore } from 'pinia'

/**
 * Frontend representation of the billing catalog snapshot. Mirrors the
 * shape returned by `/billing/catalog` (BigInt money fields arrive as
 * strings, kept as strings here — components format on render).
 */
export type BillingTermKind =
  | 'MONTHLY'
  | 'QUARTERLY'
  | 'SEMI_ANNUAL'
  | 'ANNUAL'
  | 'ON_DEMAND'

export interface CatalogTerm {
  kind: BillingTermKind
  commitment_hours: number | null
}

export type OfferingKind = 'COMPUTE' | 'VPN_GATEWAY'

export interface CatalogOffering {
  id: string
  code: string
  name: string
  description: string | null
  offering_kind: OfferingKind
  vcpu: number
  ram_mb: number
  main_disk_base_gb: number
  included_ipv4_per_vm: number
  included_ipv6_per_vm: number
  rates_by_term: Record<BillingTermKind, string | null>
}

export interface CatalogAddon {
  id: string
  code: string
  name: string
  description: string | null
  addon_type: string
  quantity_enabled: boolean
  min_quantity: number
  max_quantity: number | null
  quantity_step: number
  /**
   * Quantity comes from the chosen plan's vCPU count — never a customer choice —
   * and `rates_by_term` is the price PER vCPU. Render as on/off, priced at
   * rate × vCPU (see the Windows Server license).
   */
  quantity_follows_vcpu: boolean
  os_filter: unknown
  resizable_after_purchase: boolean
  removable_after_purchase: boolean
  rates_by_term: Record<BillingTermKind, string | null>
}

export interface CatalogTemplate {
  id: string
  code: string
  name: string
  category: string
  template_type: 'OPERATING_SYSTEM' | 'MARKETPLACE_APP'
  os_family: 'linux' | 'windows' | 'other'
  description: string | null
  icon_url: string | null
  sort_order: number
  choose_password: number
  /** Optional per-OS minimum requirements (null = no minimum). */
  min_disk_gb: number | null
  min_ram_mb: number | null
  min_vcpu: number | null
  /**
   * Release lifecycle. `version_label` is the short name shown inside its
   * distribution's version picker ("22.04 LTS") and is always present — the API
   * derives one when staff have not curated it, so the client never parses a
   * display name.
   */
  version_label: string
  is_lts: boolean
  /** The version pre-selected for a customer with no preference. One per category. */
  is_recommended: boolean
  /**
   * End of security support (YYYY-MM-DD), or null when the platform makes no
   * claim. `support_status` is null exactly when this is, and BOTH must render
   * as silence — never as a "supported" reassurance we cannot back up.
   */
  eol_at: string | null
  support_status: 'supported' | 'ending_soon' | 'ended' | null
  /**
   * When the template joined the catalog (ISO-8601), which is what marks an app
   * "Novo" in the picker. Optional: an API older than the field sends nothing,
   * and nothing is new then.
   */
  created_at?: string | null
}

export interface CatalogStorage {
  /** GB granularity the disk UI snaps to (catalog_addons.quantity_step). */
  step_gb: number
  /** Smallest extra-disk / expansion increment in GB. */
  min_extra_gb: number
  /**
   * Maximum TOTAL storage per instance in GB — main disk (base + expansion)
   * plus every additional disk, summed. Not a per-disk figure. Null = no
   * ceiling configured, and the UI must not invent one.
   */
  max_total_storage_gb?: number | null
  /** µ¢ per GB per hour of main-disk expansion, by term. Lets the checkout
   * price an expansion while the slider moves, without a simulation round-trip. */
  main_disk_rates_by_term?: Record<BillingTermKind, string | null>
  /** Same, for one GB of an additional data disk. */
  extra_disk_rates_by_term?: Record<BillingTermKind, string | null>
}

export interface CatalogSnapshot {
  terms: CatalogTerm[]
  offerings: CatalogOffering[]
  addons: CatalogAddon[]
  templates: CatalogTemplate[]
  storage?: CatalogStorage
  /** Global minimum manual recharge in BRL cents by commitment model (0 = none). */
  recharge_minimums?: {
    committed_cents: string
    on_demand_cents: string
    /** Initial on-demand credit also covers the default tier resource minimum. */
    on_demand_initial_cents?: string
  }
}

export interface SimulationLineItem {
  kind: string
  /**
   * The API's canonical, non-localized description of the line — the stable
   * identity used for analytics, and the fallback for a `kind` this build does
   * not know. What the customer READS is built by `simulationLineLabel()` from
   * the structured fields below, in the language they picked here: the pricing
   * endpoint is public, so it has no locale of ours to render into.
   */
  label: string
  /** Catalog addon this line was priced from (absent on `compute`). */
  addon_code?: string
  /** Its `addon_type` — the key `addonTypeLabel` translates addons by. */
  addon_type?: string
  /** The number the label states (see `display_unit`). Not always `quantity`,
   * which is what is BILLED after free quota. */
  display_quantity?: number
  display_unit?: 'unit' | 'gb' | 'vcpu'
  rate_micro_cents_per_hour: string
  quantity: number
  hourly_subtotal_micro_cents: string
  cycle_subtotal_micro_cents: string | null
  free_quantity_applied?: number
  rejected?: { reason: string }
}

/** An OS minimum requirement the configured instance fails to meet. */
export interface SimulationViolation {
  kind: 'min_disk_gb' | 'min_ram_mb' | 'min_vcpu'
  required: number
  actual: number
  remedy: 'increase_disk' | 'choose_larger_plan'
}

export interface SimulationQuote {
  offering_code: string
  term: BillingTermKind
  cycle_hours: number | null
  cap_applied_at_hours: number | null
  total_hourly_rate_micro_cents: string
  total_cycle_micro_cents: string | null
  line_items: SimulationLineItem[]
  warnings: string[]
  effective: { vcpu: number; ram_mb: number; disk_gb: number }
  min_requirements: { disk_gb: number | null; ram_mb: number | null; vcpu: number | null }
  violations: SimulationViolation[]
  purchasable: boolean
  /**
   * The same configuration priced on `compare_term`, when one was asked for.
   * This is the baseline every "you save X by committing" figure is measured
   * against, and it arrives with the quote rather than as a second request.
   *
   * Null when the comparison could not be priced — it is display-only, so a
   * failure there must never cost the customer the price they are buying.
   */
  comparison?: SimulationQuote | null
}

export interface SimulationInput {
  offering_code: string
  term: BillingTermKind
  /**
   * Price this same configuration on a second term too, returned as
   * `comparison` on the quote. One request, two prices — the checkout used to
   * fire a whole second simulation per slider tick for the on-demand baseline.
   */
  compare_term?: BillingTermKind
  os_choice:
    | { type: 'template'; template_code: string }
    | { type: 'byo_iso' }
    | { type: 'client_image'; client_image_id: string }
  /** Extra GB on the main disk beyond the offering base (multiple of step). */
  main_disk_extra_gb?: number
  /** Extra data disks, each sized in GB (multiple of step). */
  extra_disks?: { size_gb: number }[]
  addons?: { addon_code: string; quantity?: number }[]
  datacenter_id?: number
  connectivity_mode?: 'public' | 'private'
  vpc_id?: number
}

export interface CouponSummary {
  id: string
  code: string
  name: string
  description: string | null
  scope: 'COMMITMENT' | 'CREDIT_TOPUP'
  discount_type: 'PERCENTAGE' | 'FIXED_AMOUNT'
  percent_bps: number | null
  amount_micro_cents: string | null
  min_service_total_micro_cents: string | null
  min_topup_amount_micro_cents: string | null
  max_services: number | null
  max_redemptions: number | null
  assigned_accounts: number
  redeemed_count: number
  starts_at: string | null
  expires_at: string | null
  active: boolean
  assigned_services: number
  reserved_orders: number
}

/**
 * A recurring coupon previewed against the SERVICE being ordered: the order's
 * cycle total is that service's renewal share, which the coupon discounts on
 * every renewal. A service added to the subscription later is not discounted.
 */
export interface CouponPreview {
  valid: boolean
  reason: string | null
  coupon: CouponSummary | null
  service_total_micro_cents: string
  discount_micro_cents: string
  payable_micro_cents: string
  recurring: boolean
  message: string
}

export interface TopupCouponPreview {
  valid: boolean
  reason: string | null
  coupon: CouponSummary | null
  topup_amount_micro_cents: string
  promotional_credit_micro_cents: string
  payable_micro_cents: string
  message: string
}

export interface BalanceBuckets {
  committed_micro_cents?: string
  available_micro_cents: string
  temporary_micro_cents: string
  negative_micro_cents: string
  usable_micro_cents: string
}

export type LedgerBucket =
  | 'COMMITTED'
  | 'AVAILABLE'
  | 'TEMPORARY'
  | 'NEGATIVE'

export type LedgerReason =
  | 'USAGE'
  | 'RECHARGE'
  | 'INVOICE_CARRYOVER'
  | 'ADJUSTMENT'
  | 'REFUND'
  | 'COMMITMENT_PAYMENT'
  | 'INTERNAL_TRANSFER_IN'
  | 'INTERNAL_TRANSFER_OUT'
  | 'TEMPORARY_GRANT'
  | 'TEMPORARY_EXPIRY'
  | 'COUPON_DISCOUNT'
  | 'TOPUP_COUPON_CREDIT'
  | 'DEBT_SETTLEMENT'
  | 'COMMITMENT_EXPIRY'
  | 'RENEWAL_CREDIT_APPLIED'
  | 'PAYMENT_REVERSAL'
  | 'CARRY_FORWARD'

export interface LedgerDetail {
  /** A kind this console does not know renders as the bare reason — never as another kind. */
  kind: 'usage' | 'bandwidth' | 'adjustment' | 'resource' | 'invoice' | 'migration'
  resource_label?: string | null
  resource_type?: string | null
  /** The instance an entry exists because of: what was added, resized or removed. */
  vm_id?: number | null
  resource_ended_at?: string | null
  invoice_external_id?: string | null
  legacy_service_id?: string | null
  usage_kind?: string | null
  commitment_pool?: string | null
  rate_micro_cents_per_hour?: string | null
  seconds?: number | null
  cap_applied?: boolean
  actor?: string | null
  note?: string | null
  period_starts_at?: string | null
  period_ends_at?: string | null
  public_outbound_bytes?: string | null
  included_bytes?: string | null
  price_micro_cents_per_gb?: string | null
  late_usage_adjustment?: boolean
}

export interface LedgerEntry {
  id: string
  amount_micro_cents: string
  is_credit: boolean
  bucket: LedgerBucket
  reason: LedgerReason
  ref_type: string | null
  ref_id: string | null
  expires_at: string | null
  occurred_at: string
  balance_after_usable_micro_cents: string | null
  detail: LedgerDetail | null
}

export interface LedgerPage {
  entries: LedgerEntry[]
  next_cursor: string | null
}

export interface UsageReport {
  from: string
  to: string
  total_micro_cents: string
  daily: { day: string; total_micro_cents: string; seconds: number }[]
  by_kind: { kind: string; total_micro_cents: string; seconds: number }[]
}

export interface StatementSummary {
  from: string
  to: string
  /** Total balance (available + temporary + committed − negative) at window edges. */
  opening_total_micro_cents: string
  closing_total_micro_cents: string
  net_change_total_micro_cents: string
  /** Usable balance (available + temporary − negative) at window edges. */
  opening_usable_micro_cents: string
  closing_usable_micro_cents: string
  net_change_micro_cents: string
  /** Signed effect on the TOTAL balance per reason — chips reconcile with net_change_total. */
  by_reason: { reason: LedgerReason; amount_micro_cents: string }[]
}

export interface UsageHourItem {
  resource_label: string | null
  resource_type: string | null
  kind: string
  commitment_pool: string
  rate_micro_cents_per_hour: string
  seconds: number
  total_micro_cents: string
  cap_applied: boolean
}

export interface UsageHour {
  hour: string
  total_micro_cents: string
  seconds: number
  items: UsageHourItem[]
}

export interface UsageHourlyReport {
  from: string
  to: string
  hours: UsageHour[]
}

export interface ResourceUsage {
  id: string
  resource_type: string
  label: string | null
  resource_external_id: string | null
  commitment_pool: 'COMMITTED' | 'FLEXIBLE'
  quantity: number
  rate_micro_cents_per_hour: string
  billed_seconds_in_cycle: number
  consumed_this_cycle_micro_cents: string
  commitment_cap_micro_cents: string | null
  cap_reached: boolean
  started_at: string
}

/** A service this subscription billed and no longer does. */
export interface EndedResource {
  id: string
  resource_type: string
  label: string | null
  vm_id: number | null
  /** Null while the instance still exists. */
  instance_terminated_at: string | null
  /** The order was released before an instance existed: nothing ran, nothing was billed. */
  never_provisioned: boolean
  commitment_pool: 'COMMITTED' | 'FLEXIBLE'
  quantity: number
  rate_micro_cents_per_hour: string
  started_at: string
  ended_at: string
  billed_total_micro_cents: string
  billed_seconds: number
  /** Unused commitment the removal returned as temporary credit; null when it returned none. */
  removal_credit_micro_cents: string | null
  removal_credit_expires_at: string | null
}

export interface EndedResources {
  truncated: boolean
  items: EndedResource[]
}

/** One live grant of temporary credit: where it came from and when it expires. */
export interface TemporaryCreditGrant {
  ledger_id: string
  reason: LedgerReason
  ref_type: string | null
  granted_at: string
  expires_at: string
  granted_micro_cents: string
  /** What is left of it: temporary credit is spent earliest-expiry-first. */
  remaining_micro_cents: string
  detail: LedgerDetail | null
}

export interface TemporaryCredit {
  balance_micro_cents: string
  /** Already past its date: the next expiry sweep removes it. */
  matured_micro_cents: string
  /** Soonest expiry first. */
  grants: TemporaryCreditGrant[]
}

export interface Projection {
  forecast_as_of: string
  forecast_horizon_hours: number | null
  projected_balance_micro_cents: string | null
  balance_threshold_applies: boolean
  bandwidth_window_ends_at: string
  bandwidth_projected_until_window_end_micro_cents: string | null
  usable_micro_cents: string
  /** Observed average hourly USABLE-balance burn this cycle (FLEXIBLE pool only). */
  avg_hourly_burn_micro_cents: string
  hours_until_close: number | null
  projected_at_close_micro_cents: string | null
  tier_negative_credit_threshold_micro_cents: string
  will_breach_tier: boolean
  runway_hours: number | null
  /** Prepaid committed bucket: balance now, projected spend until close (cap-aware), remainder at close. */
  committed_balance_micro_cents: string
  committed_projected_spend_until_close_micro_cents: string | null
  committed_projected_at_close_micro_cents: string | null
  bandwidth_mode: 'unlimited' | 'fair_usage' | 'always_charged'
  /** False when bandwidth is never billed (unlimited mode) — hide usage rows. */
  bandwidth_billable: boolean
  bandwidth_burn_to_date_micro_cents: string
  /** COMMITTED overage accrued in the open window — owed, not yet in `usable`. */
  bandwidth_pending_micro_cents: string
  bandwidth_avg_hourly_micro_cents: string
  bandwidth_projected_until_close_micro_cents: string | null
}

export interface SubscriptionInvoice {
  outstanding: boolean
  id: string
  provider: string
  external_id: string
  amount_cents: string
  status: 'OPEN' | 'PAID' | 'CANCELLED' | 'REFUNDED'
  /** Always present: the API column is a non-null enum. */
  purpose: string
  /**
   * CANCELLED because the subscription balance covered it in full. The
   * obligation was met, not waived — rendered as "settled from balance".
   */
  settled_by_credit: boolean
  settled_by_credit_at: string | null
  due_at: string
  paid_at: string | null
  created_at: string
  payment_events: {
    id: string
    event_type: string
    source: string
    received_at: string
  }[]
}

export interface SubscriptionSuspension {
  id: string
  reason: string
  suspended_at: string
  resumed_at: string | null
  /** Why an operator suspended it, written for the customer. Null when automatic. */
  customer_reason: string | null
}

/** Set when the client scheduled the subscription for end-of-cycle cancellation. */
export interface PendingCancellation {
  requested_at: string
  /** Null = ON_DEMAND subscription, effective immediately. */
  effective_at: string | null
}

/**
 * A plan change support scheduled for the cycle end at the customer's
 * request. Nothing changes before that date; the renewal issued on it covers
 * the full first cycle of the new term.
 */
export interface ScheduledPlanChange {
  effective_at: string | null
  new_commitment_type: 'COMMITTED' | 'ON_DEMAND'
  new_term_kind: string
}

export interface CancellationRequest {
  id: string
  subscription_id: string
  status: 'pending' | 'withdrawn' | 'completed'
  reason: string
  comment: string | null
  effective_at: string | null
  created_at: string
  withdrawn_at: string | null
  completed_at: string | null
}

export interface SubscriptionDetail {
  payment_hold?: { holds_until: string; holds_until_date: string } | null;
  trial?: import("~/generated-api-client").TrialSubscriptionDto | null;
  id: string;
  name: string | null;
  status: string;
  commitment_type: string;
  term: { kind: BillingTermKind } | null;
  tier: { name: string; threshold_micro_cents: string } | null;
  current_cycle_starts_at: string;
  current_cycle_ends_at: string | null;
  balance: BalanceBuckets;
  pending_cancellation: PendingCancellation | null;
  scheduled_plan_change?: ScheduledPlanChange | null;
  /** When the ledger starts and when it last moved — what the statement's opening range is chosen from. */
  ledger?: { first_entry_at: string | null; last_entry_at: string | null };
  /** Where the temporary balance came from and when each part of it expires. */
  temporary_credit?: TemporaryCredit;
  active_resources: {
    id: string
    resource_type: string
    commitment_pool: string
    rate_micro_cents_per_hour: string
    quantity: number
    started_at: string
  }[]
}

export interface SubscriptionSummary {
  trial?: import("~/generated-api-client").TrialSubscriptionDto | null;
  id: string;
  name: string | null;
  status: string;
  commitment_type: string;
  term: { kind: BillingTermKind } | null;
  current_cycle_starts_at: string | null;
  current_cycle_ends_at: string | null;
  balance: BalanceBuckets | null;
  resources: {
    total: number
    instance_count: number
    /** Up to 3 VM display names; the remainder is implied by `instance_count`. */
    labels: string[]
    counts_by_type: Record<string, number>
  }
  recurring: {
    monthly_micro_cents: string
    /** Charged at renewal. Null on ON_DEMAND, which has no commitment. */
    term_micro_cents: string | null
  }
  cycle: {
    days_remaining: number | null
    consumed_micro_cents: string
    /** Denominator of the consumption bar. Null on ON_DEMAND. */
    commitment_cap_micro_cents: string | null
  }
  invoices: {
    open_count: number
    next_due_at: string | null
    overdue: boolean
  }
  pending_cancellation: PendingCancellation | null
  /**
   * An operator has given this account extra time to pay. Account-scoped, so
   * every subscription in the list carries the same one.
   *
   * Nothing about the money changes while it runs — the invoices are still
   * open, still owed, and usage keeps being billed.
   */
  payment_hold: PaymentHold | null
}

/** A payment extension, as the customer sees it. */
export interface PaymentHold {
  holds_until: string
  /**
   * The last day covered, `YYYY-MM-DD` in the platform's time zone. This is
   * what the banner prints: `holds_until` is an instant a few hours into the
   * next UTC day, which formats as the wrong date in most browsers.
   */
  holds_until_date: string
  days_remaining: number
  /** It switched suspended services back on, rather than only deferring. */
  lifted_suspension: boolean
}

/** One provider invoice of the account, labelled with its subscription. */
export interface AccountInvoice {
  id: string
  provider: string
  /** For WHMCS, the id accepted by viewinvoice.php. */
  external_id: string
  amount_cents: string
  status: string
  /** Always present: the API column is a non-null enum. */
  purpose: string
  settled_by_credit: boolean
  settled_by_credit_at: string | null
  due_at: string
  paid_at: string | null
  created_at: string
  overdue: boolean
  subscription: { id: string; name: string | null } | null
}

/** What `settleRenewalFromBalance` did, or why it did nothing. */
export interface RenewalCreditSettleResult {
  status: 'settled' | 'noop'
  reason?:
    | 'disabled'
    | 'no_open_renewal'
    | 'insufficient_balance'
    | 'lock_busy'
    | 'order_in_flight'
    | 'remote_not_open'
    | 'provider_unavailable'
  invoice_external_id?: string
  shortfall_cents?: string
  settled_invoices: { external_id: string; applied_cents: string }[]
}

/** Order placed immediately from subscription balance */
export interface OrderResultProvisioned {
  status: 'provisioned'
  orderId: string
  subscriptionId: string
  resourceIds: string[]
  quote: SimulationQuote
  coupon: CouponPreview | null
}

/** Order awaiting WHMCS invoice payment */
export interface OrderResultPendingPayment {
  status: 'pending_payment'
  orderId: string
  totalCents: string
  grossCents?: string
  discountCents?: string
  /**
   * Add to an existing subscription only: how much of the subscription's own
   * balance the server netted against the charge, so `totalCents` is
   * `grossCents − balanceAppliedCents`. Drawn when the invoice is paid.
   */
  balanceAppliedCents?: string
  /**
   * Minimum recharge in force (cents), including a new on-demand subscription.
   */
  minimumRechargeCents?: string
  /** Add-to-existing only: credit above the net charge due to the minimum. */
  minimumRechargeCreditCents?: string
  /** Add-to-existing only: hours of the current cycle the charge covers. */
  chargedHours?: number | null
  /** Add-to-existing only: when the current cycle ends (ISO), the renewal date. */
  cycleEndsAt?: string | null
  /** Add-to-existing only: what the instance renews at, a full period in cents. */
  termCents?: string
  invoiceExternalId: string
  payUrl?: string | null
  quote: SimulationQuote
  coupon: CouponPreview | null
}

/** Balance too low — client should top up and retry */
export interface OrderResultRequiresTopup {
  status: 'requires_topup'
  subscriptionId: string
  shortfallCents: string
  usableCents: string
  requiredCents: string
  quote: SimulationQuote
  coupon: CouponPreview | null
}

export type CreateOrderResult =
  | OrderResultProvisioned
  | OrderResultPendingPayment
  | OrderResultRequiresTopup

/**
 * Where a placed order stands, for the "waiting for payment" panel. Reading it
 * never advances the order — the reconciler owns payment observation.
 */
export interface OrderPaymentState {
  order_id: string
  initial_credit_cents?: string | null
  status:
    | 'pending_payment'
    | 'paid'
    | 'provisioning'
    | 'provisioned'
    | 'failed'
    | 'cancelled'
  paid: boolean
  provisioned: boolean
  failed: boolean
  expires_at?: string | null
  subscription_id?: string | null
  resource_ids: string[]
  invoice?: {
    external_id: string
    status: string
    amount_cents: string
    due_at?: string | null
    paid_at?: string | null
  } | null
  /**
   * Subscription balance netted against the invoice of an add-to-existing
   * order, in cents; null when nothing was netted.
   */
  balance_applied_cents?: string | null
  /** What the minimum recharge added above the net charge (cents); stays as credit once paid. */
  minimum_recharge_credit_cents?: string | null
  /** False when a refresh was asked for but the per-invoice cooldown blocked it. */
  checked_provider: boolean
}

export const useBillingStore = defineStore('billing', () => {
  const { billingCurrency, billingLocale } = useRuntimeConfig().public
  const catalog = ref<CatalogSnapshot | null>(null)
  const loadingCatalog = ref(false)
  const subscriptions = ref<SubscriptionSummary[]>([])
  const loadingSubscriptions = ref(false)
  let subscriptionsRequestId = 0
  const lastQuote = ref<SimulationQuote | null>(null)
  const lastOrder = ref<CreateOrderResult | null>(null)
  const error = ref<string | null>(null)

  function getMwApi() {
    const { $mwApi } = useNuxtApp()
    return $mwApi as any
  }

  async function fetchCatalog(): Promise<CatalogSnapshot> {
    loadingCatalog.value = true
    error.value = null
    try {
      const res = await getMwApi().billingCatalogSnapshot()
      catalog.value = res.data as CatalogSnapshot
      return catalog.value
    } catch (err: any) {
      error.value = err?.response?.data?.message ?? err.message
      throw err
    } finally {
      loadingCatalog.value = false
    }
  }

  async function fetchSubscriptions(
    status?: string
  ): Promise<SubscriptionSummary[]> {
    const requestId = ++subscriptionsRequestId
    loadingSubscriptions.value = true
    try {
      const res = await getMwApi().billingListSubscriptions(status)
      const result = res.data as SubscriptionSummary[]
      if (requestId === subscriptionsRequestId) {
        subscriptions.value = result
      }
      return result
    } catch (err: any) {
      if (requestId === subscriptionsRequestId) {
        error.value = err?.response?.data?.message ?? err.message
      }
      throw err
    } finally {
      if (requestId === subscriptionsRequestId) {
        loadingSubscriptions.value = false
      }
    }
  }

  async function updateSubscriptionName(
    subscriptionId: string,
    name: string | null
  ): Promise<void> {
    await getMwApi().billingUpdateSubscription(subscriptionId, { name })
  }

  async function fetchSubscriptionsForCheckout(): Promise<
    SubscriptionSummary[]
  > {
    // The checkout endpoint returns only a bare summary (no name rollups); the
    // billing list already computes names, instance counts and monthly cost —
    // the picker needs those, so reuse it. The statuses must be explicit and
    // must match what the API's add-to-existing order path accepts:
    // `active` AND `inactive`. An inactive subscription is one parked after
    // its last instance was deleted — it still holds the removal's temporary
    // credit and any prepaid balance, and adding an instance to it is exactly
    // how that money is used. Asking for `active` only is how a customer who
    // deleted their only instance lost sight of R$ 79 of credit: the picker
    // never offered the subscription, and every new order went out as a
    // brand-new subscription with a fresh invoice. The server default would
    // add `suspended`, which the order path refuses, so it is not used.
    const res = await getMwApi().billingListSubscriptions('active,inactive')
    // The order path also refuses a subscription scheduled for end-of-cycle
    // cancellation (its resources would be destroyed with it), so it is not
    // offered either.
    return (res.data as SubscriptionSummary[]).filter(
      (sub) => !sub.pending_cancellation && (!sub.trial || !!sub.trial.converted_at)
    )
  }

  async function topupSubscription(
    subscriptionId: string,
    amountCents: number,
    couponCode?: string,
    intent?: {
      kind: 'UPGRADE' | 'RENEWAL_PREPAY'
      vmId?: number
      /** RENEWAL_PREPAY: the cycle end being prepaid (renewal-preview.cycle_ends_at). */
      cycleEndsAt?: string | null
    }
  ) {
    const res = await getMwApi().billingTopupSubscription(subscriptionId, {
      amount_cents: amountCents,
      coupon_code: couponCode || undefined,
      intent_kind: intent?.kind as any,
      intent_vm_id: intent?.vmId,
      intent_cycle_ends_at: intent?.cycleEndsAt ?? undefined,
    })
    return res.data as {
      invoice_external_id: string
      pay_url: string | null
      amount_cents: string
      gross_amount_cents?: string
      discount_cents?: string
      coupon?: TopupCouponPreview | null
      due_at: string
    }
  }

  /**
   * Next-renewal preview for the "Antecipar renovação" flow: how much the
   * next cycle-close will invoice, how much the current available balance
   * already covers, and whether the renewal invoice is ALREADY open (in which
   * case the customer should pay it instead of prepaying).
   */
  async function getRenewalPreview(subscriptionId: string) {
    const res = await getMwApi().billingGetRenewalPreview(subscriptionId)
    return res.data
  }

  /**
   * Settle the open renewal invoice from the subscription balance now — the
   * same settlement the platform runs on every credit landing and hourly.
   * Only ever settles in full; a shortfall comes back as `noop`.
   */
  async function settleRenewalFromBalance(subscriptionId: string) {
    const res = await getMwApi().billingSettleRenewalFromBalance(subscriptionId)
    return res.data as RenewalCreditSettleResult
  }

  /** Void an unpaid top-up invoice. 409 when the provider already reports it paid. */
  async function cancelTopupInvoice(subscriptionId: string, externalId: string) {
    const res = await getMwApi().billingCancelTopupInvoice(subscriptionId, externalId)
    return res.data as { invoice_external_id: string }
  }

  async function previewTopupCoupon(
    subscriptionId: string,
    input: {
      coupon_code: string
      topup_amount_micro_cents: string
    }
  ): Promise<TopupCouponPreview> {
    const res = await getMwApi().billingPreviewTopupCoupon(subscriptionId, input)
    return res.data as TopupCouponPreview
  }

  async function simulate(input: SimulationInput): Promise<SimulationQuote> {
    const res = await getMwApi().billingCatalogSimulate(input)
    lastQuote.value = res.data as SimulationQuote
    return lastQuote.value
  }

  /**
   * Prices a configuration WITHOUT making it the current quote. Used to price
   * the same configuration on another term (the on-demand baseline the
   * checkout's savings figures are measured against), which must never
   * overwrite what the customer is actually buying.
   */
  async function simulatePreview(
    input: SimulationInput
  ): Promise<SimulationQuote> {
    const res = await getMwApi().billingCatalogSimulate(input)
    return res.data as SimulationQuote
  }

  async function createOrder(input: {
    trial_code?: string;
    simulation: SimulationInput;
    target_subscription_id?: string;
    vm_password?: string;
    name?: string;
    coupon_code?: string;
    /** Initial prepaid balance for a new on-demand subscription, in cents. */
    initial_credit_cents?: string
    /**
     * Makes this call safe to send twice.
     *
     * The endpoint had no replay protection, so a network retry produced two
     * orders and two unpaid invoices for one server. Optional here and on the
     * API: without a key the call behaves exactly as it always did.
     */
    idempotency_key?: string
  }): Promise<CreateOrderResult> {
    // Forward the affiliate referral code captured from the recommendation-link
    // cookie (set by the affiliate-ref plugin). Tracking only — ignored by the
    // API for unknown codes and self-referrals.
    const affiliateRef = useCookie<string | null>('mw_affiliate_ref').value
    // Forward Google Ads / GA4 attribution (gclid/utm captured by the
    // marketing-attribution plugin) + the GA4 client id, so the server can fire
    // the ad-blocker-proof purchase. Tracking only — never affects pricing.
    const attribution =
      useCookie<Record<string, string> | null>('mw_marketing_attribution')
        .value ?? undefined
    const gaClientId = await useAnalytics().getClientId()
    const marketing_attribution =
      attribution || gaClientId
        ? { ...attribution, ...(gaClientId ? { ga_client_id: gaClientId } : {}) }
        : undefined
    const { idempotency_key: idempotencyKey, ...body } = input
    const res = await getMwApi().checkoutV2CreateOrder(
      {
        ...body,
        ...(affiliateRef ? { affiliate_ref: affiliateRef } : {}),
        ...(marketing_attribution ? { marketing_attribution } : {}),
      } as typeof body,
      // Second positional argument: the generator turns the endpoint's
      // `@ApiHeader` into a typed parameter and sets the header itself. The BFF
      // forwards it explicitly — that proxy is an allowlist, so a header it
      // does not name is dropped without a word.
      idempotencyKey,
    )
    lastOrder.value = res.data as CreateOrderResult
    return lastOrder.value
  }

  async function previewCoupon(input: {
    coupon_code: string
    service_total_micro_cents: string
    target_subscription_id?: string
  }): Promise<CouponPreview> {
    const res = await getMwApi().checkoutV2PreviewCoupon(input)
    return res.data as CouponPreview
  }

  async function confirmOrder(orderId: string) {
    const res = await getMwApi().checkoutV2ConfirmOrder(orderId)
    return res.data
  }

  /**
   * Poll-safe read of a placed order's payment/provisioning state.
   * `refresh` additionally asks the invoice provider (rate-limited server-side)
   * — reserve it for an explicit "I already paid" click.
   */
  async function fetchOrderPaymentState(
    orderId: string,
    refresh = false
  ): Promise<OrderPaymentState> {
    const res = await getMwApi().checkoutV2GetOrderPaymentState(orderId, refresh)
    return res.data as OrderPaymentState
  }

  // ── Accounting / transparency reads ────────────────────────────────────────

  async function fetchSubscription(id: string): Promise<SubscriptionDetail> {
    const res = await getMwApi().billingGetSubscription(id)
    return res.data as SubscriptionDetail
  }

  /**
   * `comment` is REQUIRED by the API: the exit survey's enum is a category and
   * the free text is the finding, so the endpoint refuses a request without
   * one. Sent already trimmed — the API trims too, but a store that forwards
   * padding would let whitespace look like an answer on the way out.
   */
  async function requestCancellation(
    subscriptionId: string,
    input: { reason: string; comment: string },
  ): Promise<CancellationRequest> {
    const res = await getMwApi().billingRequestSubscriptionCancellation(subscriptionId, {
      reason: input.reason,
      comment: input.comment.trim(),
    })
    return res.data as CancellationRequest
  }

  async function withdrawCancellation(subscriptionId: string): Promise<CancellationRequest> {
    const res = await getMwApi().billingWithdrawSubscriptionCancellation(subscriptionId)
    return res.data as CancellationRequest
  }

  async function fetchCancellation(
    subscriptionId: string,
  ): Promise<CancellationRequest | null> {
    const res = await getMwApi().billingGetSubscriptionCancellation(subscriptionId)
    return (res.data?.pending ?? null) as CancellationRequest | null
  }

  async function fetchLedger(
    id: string,
    opts: {
      limit?: number
      cursor?: string
      bucket?: LedgerBucket
      reason?: LedgerReason
      from?: string
      to?: string
    } = {}
  ): Promise<LedgerPage> {
    const res = await getMwApi().billingGetSubscriptionLedger(
      id,
      opts.limit,
      opts.cursor,
      opts.bucket,
      opts.reason,
      opts.from,
      opts.to
    )
    return res.data as LedgerPage
  }

  async function fetchUsage(
    id: string,
    opts: { from?: string; to?: string } = {}
  ): Promise<UsageReport> {
    const res = await getMwApi().billingGetSubscriptionUsage(id, opts.from, opts.to)
    return res.data as UsageReport
  }

  async function fetchStatementSummary(
    id: string,
    opts: { from?: string; to?: string } = {}
  ): Promise<StatementSummary> {
    const res = await getMwApi().billingGetSubscriptionStatementSummary(id, opts.from, opts.to)
    return res.data as StatementSummary
  }

  async function fetchUsageHourly(
    id: string,
    opts: { from?: string; to?: string } = {}
  ): Promise<UsageHourlyReport> {
    const res = await getMwApi().billingGetSubscriptionUsageHourly(id, opts.from, opts.to)
    return res.data as UsageHourlyReport
  }

  /** Triggers a browser download of the period ledger as CSV. */
  async function downloadLedgerCsv(
    id: string,
    opts: { from?: string; to?: string } = {}
  ): Promise<void> {
    const res = await getMwApi().billingExportSubscriptionLedgerCsv(id, opts.from, opts.to, {
      responseType: 'blob',
    })
    const blob = new Blob([res.data as BlobPart], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `extrato-${id}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  async function fetchResources(id: string): Promise<ResourceUsage[]> {
    const res = await getMwApi().billingGetSubscriptionResources(id)
    return res.data as ResourceUsage[]
  }

  /** What this subscription USED to bill: removed services and what each removal returned. */
  async function fetchEndedResources(id: string): Promise<EndedResources> {
    const res = await getMwApi().billingGetSubscriptionEndedResources(id)
    return res.data as unknown as EndedResources
  }

  async function fetchProjection(id: string): Promise<Projection> {
    const res = await getMwApi().billingGetSubscriptionProjection(id)
    return res.data as Projection
  }

  async function fetchInvoices(id: string): Promise<SubscriptionInvoice[]> {
    const res = await getMwApi().billingGetSubscriptionInvoices(id)
    return res.data as SubscriptionInvoice[]
  }

  async function fetchAccountInvoices(status?: string): Promise<AccountInvoice[]> {
    const res = await getMwApi().billingListAccountInvoices(status)
    return res.data as AccountInvoice[]
  }

  async function fetchSuspensions(id: string): Promise<SubscriptionSuspension[]> {
    const res = await getMwApi().billingGetSubscriptionSuspensions(id)
    return res.data as SubscriptionSuspension[]
  }

  /** Formats a µ¢ string as the configured billing currency. */
  function formatMicroCents(micro: string | null | undefined): string {
    if (micro == null) return '—'
    const value = Number(BigInt(micro)) / 1_000_000
    return new Intl.NumberFormat(billingLocale, {
      style: 'currency',
      currency: billingCurrency,
    }).format(value)
  }

  /** Formats an Int cents string as the configured billing currency. */
  function formatCents(cents: string | null | undefined): string {
    if (cents == null) return '—'
    const value = Number(BigInt(cents)) / 100
    return new Intl.NumberFormat(billingLocale, {
      style: 'currency',
      currency: billingCurrency,
    }).format(value)
  }

  return {
    catalog,
    loadingCatalog,
    subscriptions,
    loadingSubscriptions,
    lastQuote,
    lastOrder,
    error,
    fetchCatalog,
    fetchSubscriptions,
    updateSubscriptionName,
    fetchSubscriptionsForCheckout,
    topupSubscription,
    getRenewalPreview,
    settleRenewalFromBalance,
    cancelTopupInvoice,
    previewTopupCoupon,
    simulate,
    simulatePreview,
    createOrder,
    previewCoupon,
    confirmOrder,
    fetchOrderPaymentState,
    fetchSubscription,
    requestCancellation,
    withdrawCancellation,
    fetchCancellation,
    fetchLedger,
    fetchUsage,
    fetchStatementSummary,
    fetchUsageHourly,
    downloadLedgerCsv,
    fetchResources,
    fetchEndedResources,
    fetchProjection,
    fetchInvoices,
    fetchAccountInvoices,
    fetchSuspensions,
    formatMicroCents,
    formatCents,
  }
})
